import { SettingOutlined } from "@ant-design/icons";
import { Button, Form, FormInstance, Modal } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DynamicModel,
  useBeaconConfiguration,
  useConfigureBeacon,
} from "../../api";
import { ConfigFormSingle } from "./ConfigFormSingle";
import { UploadFile } from "../UploadFile";
import { DownloadFile } from "../DownloadFile";
import { BeaconConfigFile } from "../../api/models";
interface BeaconConfigModalSingleProps {
  selectedBeaconSerial: string;
}

export const BeaconConfigModalSingle = ({
  selectedBeaconSerial,
}: BeaconConfigModalSingleProps) => {
  const { t, i18n } = useTranslation();

  const [form] = Form.useForm();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavedDisabled, setIsSavedDisabled] = useState(true);
  const [formValues, setFormValues] = useState<BeaconConfigFile | null>(null);

  const success = () => {
    Modal.success({
      title: t("beacon:configure-success-title"),
      content: t("beacon:configure-success-body"),
      onOk() {
        setIsOpen(false);
      },
    });
  };

  const error = () => {
    Modal.error({
      title: t("error"),
      content: t("beacon:configure-failed-title"),
    });
  };

  const setFormDataReady = (ready: boolean) => {
    setIsSavedDisabled(!ready);
  };

  const configure = useConfigureBeacon();

  const saveConfiguration = async () => {
    const values = form.getFieldsValue();
    const desiredConfig = {
      activityInterval: Number(values["activityInterval"]),
      inactivityInterval: Number(values["inactivityInterval"]),
      heartbeat: Number(values["heartbeat"]),
      ignoreMotion: Number(values["ignoreMotion"]),
      accelerometer: {
        duration: Number(values["accelerometer.duration"]),
        threshold: Number(values["accelerometer.threshold"]),
        x: Boolean(values["accelerometer.x"]),
        y: Boolean(values["accelerometer.y"]),
        z: Boolean(values["accelerometer.z"]),
      },
      positioning: {
        heartbeat: Number(values["positioning.heartbeat"]),
        ttffTimeout: Number(values["positioning.ttffTimeout"]),
        ttffRetries: Number(values["positioning.ttffRetries"]),
        fixQualityTimeout: Number(values["positioning.fixQualityTimeout"]),
        dynamicModel: Number(values["positioning.dynamicModel"]),
      },
      rtk: {
        protocol: "ntrip-v2",
        credentials: {
          host: values["rtk.credentials.host"],
          port: Number(values["rtk.credentials.port"]),
          mountpoint: values["rtk.credentials.mountpoint"],
          user: values["rtk.credentials.user"],
          password: values["rtk.credentials.password"],
        },
      },
    };

    setIsSaving(true);
    const result = await configure({
      beaconSerial: selectedBeaconSerial,
      configuration: desiredConfig,
    });
    setIsSaving(false);

    if (result.error) error();
    else {
      setFormValues(null);
      success();
    }
  };

  const resetModal = () => {
    form.resetFields();
    setFormValues(null);
    setIsSavedDisabled(false);
    setIsOpen(false);
  };

  const setConfigData = async (data: BeaconConfigFile) => {
    // Ensure dynamicModel from uploaded file is valid, if not default to STATIONARY
    if (
      data["positioning.dynamicModel"] !== undefined &&
      !Object.values(DynamicModel).includes(
        data["positioning.dynamicModel"] as DynamicModel
      )
    ) {
      data["positioning.dynamicModel"] = DynamicModel.STATIONARY;
    }
    form.setFieldsValue(data);

    setFormValues(data);

    try {
      await form.validateFields();
    } catch (error) {
      setIsSavedDisabled(true);
    }
  };

  const handleValuesChange = (
    changedValues: BeaconConfigFile,
    allValues: BeaconConfigFile
  ) => {
    setFormValues(allValues);
  };

  return (
    <span data-testid="config-modal">
      <SettingOutlined
        onClick={() => {
          setIsOpen(true);
        }}
        title="Configure"
      />
      <Modal
        width="50%"
        style={{
          top: Math.max(16, window.innerHeight * 0.15 - 77),
          minWidth: 660,
        }}
        bodyStyle={{
          overflowY: "auto",
        }}
        title={t("beacon:configuration-title", {
          type: i18n.language === "de" ? t("beacon-capitalized") : t("beacon"),
          serial_or_id: selectedBeaconSerial,
        })}
        open={isOpen}
        onCancel={() => resetModal()}
        onOk={saveConfiguration}
        destroyOnClose
        confirmLoading={isSaving}
        footer={[
          <BeaconConfigurationFooter
            key="footer"
            formValues={formValues ?? form.getFieldsValue()}
            selectedBeaconSerial={selectedBeaconSerial}
            isSavedDisabled={isSavedDisabled}
            isSaving={isSaving}
            setConfigData={setConfigData}
            saveConfiguration={saveConfiguration}
            resetModal={resetModal}
          />,
        ]}
      >
        <FormWrapper
          form={form}
          setFormDataReady={setFormDataReady}
          selectedBeaconSerial={selectedBeaconSerial}
          onValuesChange={handleValuesChange}
        />
      </Modal>
    </span>
  );
};

/*
  Ant Desing modal is rendered on load of page, so we need to use a wrapper
  component to fetch the configuration and pass it to the form to prevent the 
  hook useBeaconConfiguration from being called before the modal is opened.

  The hook call was used in the form component, but with addition of SCB-one
  configuration, the form needs to have passed the configuration as a prop, so 
  the form can be reused for both SCB-one and SCB-one devices.
*/
interface FormWrapperProps {
  selectedBeaconSerial: string;
  form: FormInstance<any>;
  setFormDataReady: (ready: boolean) => void;
  onValuesChange: (changedValues: any, allValues: any) => void;
}

function FormWrapper({
  selectedBeaconSerial,
  form,
  setFormDataReady,
  onValuesChange,
}: FormWrapperProps) {
  const configuration = useBeaconConfiguration(selectedBeaconSerial);
  return (
    <ConfigFormSingle
      context="beacon"
      configuration={configuration}
      form={form}
      setFormDataReady={setFormDataReady}
      onValuesChange={onValuesChange}
    />
  );
}

interface BeaconConfigurationFooterProps {
  selectedBeaconSerial: string;
  formValues: BeaconConfigFile;
  isSavedDisabled: boolean;
  isSaving: boolean;
  saveConfiguration: () => void;
  resetModal: () => void;
  setConfigData: (data: BeaconConfigFile) => void;
}

export function BeaconConfigurationFooter({
  selectedBeaconSerial,
  formValues,
  isSavedDisabled,
  isSaving,
  saveConfiguration,
  resetModal,
  setConfigData,
}: BeaconConfigurationFooterProps) {
  const { t } = useTranslation();

  return (
    <div
      key="footer-container"
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
      }}
    >
      <div style={{ display: "flex", gap: "8px" }}>
        <UploadFile setConfigData={setConfigData} />
        <DownloadFile
          fileContent={JSON.stringify(formValues, null, 2)}
          fileName={`config-${selectedBeaconSerial}.json`}
          fileType="application/json"
        />
      </div>
      <div key="button-group" style={{ display: "flex", gap: "8px" }}>
        <Button key="cancel-button" onClick={() => resetModal()}>
          {t("translations:cancel")}
        </Button>
        <Button
          key="save-button"
          type="primary"
          onClick={saveConfiguration}
          disabled={isSavedDisabled}
          loading={isSaving}
        >
          {t("translations:save")}
        </Button>
      </div>
    </div>
  );
}
